// src/pages/Articles/components/Sidebar.jsx
import React from "react";
import { Link } from "react-router-dom";
import styles from "../../../../styles/pages/articles/ArticleHome.module.scss";

/** Small helper */
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");

export function SidebarSection({ title, children, style }) {
  return (
    <div className={styles.sideCard} style={style}>
      <h3 className={styles.sideTitle}>{title}</h3>
      {children}
    </div>
  );
}

/** -------------------------
 *  Home page: Filters Sidebar
 *  ------------------------- */
export function ArticleFiltersSidebar({
  categories = [],
  topics = [],
  popularTags = [],
  selectedCategory,
  selectedTopic,
  selectedTag,
  featuredOnly,
  onSelectCategory,
  onSelectTopic,
  onToggleTag,
  onToggleFeatured,
  onClear,
}) {
  const SideItem = ({ current, onClick, children }) => (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.sideItem} ${current ? styles.sideItemCurrent : ""}`}
    >
      {children}
    </button>
  );

  return (
    <aside className={styles.sidebar}>
      <SidebarSection title="Categories">
        <div className={styles.sideList}>
          {categories.map((c) => (
            <SideItem
              key={`cat-${c}`}
              current={selectedCategory === c}
              onClick={() => onSelectCategory?.(c)}
            >
              {cap(c)}
            </SideItem>
          ))}
        </div>
      </SidebarSection>

      <SidebarSection title="Topics" style={{ marginTop: ".9rem" }}>
        <div className={styles.sideList}>
          {topics.map((t) => (
            <SideItem
              key={`t-${t}`}
              current={selectedTopic === t}
              onClick={() => onSelectTopic?.(t)}
            >
              {cap(t)}
            </SideItem>
          ))}
        </div>
      </SidebarSection>

      <SidebarSection title="Popular tags" style={{ marginTop: ".9rem" }}>
        <div className={styles.sideRow}>
          {popularTags.map((t) => (
            <button
              type="button"
              key={`tag-${t}`}
              className={styles.tag}
              style={{ outline: selectedTag === t ? "2px solid var(--teal)" : "none" }}
              onClick={() => onToggleTag?.(t)}
            >
              {t}
            </button>
          ))}
        </div>
        <div className={styles.ctaNote} style={{ marginTop: ".6rem" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3v2m0 14v2M21 12h-2M5 12H3"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className={styles.muted}>Click any tag to filter.</span>
        </div>
      </SidebarSection>

      <SidebarSection title="Quick actions" style={{ marginTop: ".9rem" }}>
        <div className={styles.sideRow}>
          <button
            type="button"
            className={styles.tag}
            style={{ background: "#e9fff9", color: "#0d5048", borderColor: "rgba(13,80,72,.2)" }}
            onClick={onClear}
          >
            Clear filters
          </button>
          <button
            type="button"
            className={styles.tag}
            style={{ outline: featuredOnly ? "2px solid var(--teal)" : "none" }}
            onClick={() => onToggleFeatured?.()}
          >
            Show featured only
          </button>
        </div>
      </SidebarSection>
    </aside>
  );
}

/** ------------------------------
 *  Single page: Right TOC Sidebar
 *  ------------------------------ */
export function ArticleRightSidebar({
  toc = [],
  meta,
  related = [], // array of {id,title}
  showBackCard = true,
}) {
  return (
    <aside className={styles.sidebar}>
      <SidebarSection title="On this page">
        <nav className={styles.sideList} aria-label="Table of contents">
          {toc.length === 0 && <span className={styles.muted}>Sections will appear here</span>}
          {toc.map((h) => (
            <a
              key={h.id}
              href={`#${h.id}`}
              className={styles.sideItem}
              style={{ paddingLeft: h.level === 3 ? "1.1rem" : undefined }}
            >
              {h.text}
            </a>
          ))}
        </nav>
      </SidebarSection>

      <SidebarSection title="Meta" style={{ marginTop: ".9rem" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: ".45rem" }}>
          {(meta?.tags || []).map((t) => (
            <span key={`tag-${t}`} className={styles.tag}>
              {t}
            </span>
          ))}
        </div>
      </SidebarSection>

      {related.length > 0 && (
        <SidebarSection title="More like this" style={{ marginTop: ".9rem" }}>
          <div className={styles.sideList}>
            {related.map((r) => (
              <Link key={r.id} to={`/article/${r.id}`} className={styles.sideItem}>
                {r.title}
              </Link>
            ))}
          </div>
        </SidebarSection>
      )}

      {showBackCard && (
        <SidebarSection title=" " style={{ marginTop: ".9rem" }}>
          <Link to="/article" className={styles.sideItem}>
            ← Back to Articles
          </Link>
        </SidebarSection>
      )}
    </aside>
  );
}
